import { Badge } from 'src/design/atoms/Badge';
import { Card } from 'src/design/atoms/Card';
import { Grid } from 'src/design/atoms/Grid';
import { Checkbox } from 'src/design/atoms/Checkbox';
import React from 'react';
import { useChecklist } from 'react-checklist';
import { JobUserDto, OrganizationRoleDto } from 'src/api/FiretrolApiClient';
import { useJobTeamByRoles } from 'src/api/queries/jobs/useJobTeamByRoles';
import { RoleSelectorV2 } from 'src/app/selectors/RoleSelectorV2';
import { useJobs } from 'src/hooks/useJobs';
import classes from './RoleUserSelector.module.css';

export interface IRoleUserSelectorProps {
  field: string;
  onFieldChange: (field: string, value: OrganizationRoleDto[]) => void;
  onUserSelect?: (users: JobUserDto[]) => void;
  initialSelectedRoles?: OrganizationRoleDto[];
  selectedRoles: OrganizationRoleDto[];
  initialUsers?: JobUserDto[];
  selectDescription: string;
  placeholder?: string;
}

/** @registry Role-select paired with an exclusion checklist of the users those roles resolve to, for a job's workflow. */
export const RoleUserSelector: React.FC<IRoleUserSelectorProps> = ({
  onFieldChange,
  placeholder = '',
  onUserSelect,
  selectedRoles,
  selectDescription,
  field,
  initialSelectedRoles = [],
  initialUsers = [],
}) => {
  const { selectedJob, teamV2 } = useJobs();
  const roleIds = React.useMemo(
    () => selectedRoles.map((r) => r.id),
    [selectedRoles],
  );
  const { data: roleUsers } = useJobTeamByRoles(selectedJob?.id, roleIds);
  const users: JobUserDto[] = roleUsers ?? initialUsers;
  const { handleCheck, isCheckedAll, checkedItems, setCheckedItems } =
    useChecklist(teamV2, {
      key: 'userId',
      keyType: 'number',
    });

  React.useEffect(() => {
    if (onUserSelect) {
      const selected = teamV2.filter((teamUser) =>
        checkedItems.has(teamUser.user.id),
      );
      onUserSelect(selected);
    }
  }, [checkedItems]);

  React.useEffect(() => {
    const set = new Set<number>();
    initialUsers.forEach((val) => set.add(val.user.id));
    setCheckedItems(set);
  }, []);
  React.useEffect(() => {
    checkedItems.clear();
  }, [roleUsers]);

  return (
    <>
      <RoleSelectorV2
        placeholder={placeholder}
        onChange={(value) => onFieldChange(field, value)}
        value={selectedRoles}
        initialValue={initialSelectedRoles}
      />
      {users.length > 0 && (
        <Card p={0}>
          <div className={classes.checkboxContainer}>
            <p>{selectDescription}</p>
            <Checkbox
              checked={isCheckedAll}
              label="Exclude All"
              onChange={handleCheck}
            />
            {users.map((v) => (
              <Grid key={v.id}>
                <Grid.Col>
                  <Checkbox
                    checked={checkedItems.has(v.user.id)}
                    label={`${v.user.firstName} ${v.user.lastName}`}
                    onChange={handleCheck}
                    data-key={v.user.id}
                  />
                </Grid.Col>
                <Grid.Col>
                  {v.user.userOrganizationRole
                    ?.filter((r) => !!r.organizationRole)
                    .map((r) => r.organizationRole)
                    .map((r) => {
                      return (
                        <Badge key={r.id} variant="light" mr="xs">
                          {r.name}
                        </Badge>
                      );
                    })}
                </Grid.Col>
              </Grid>
            ))}
          </div>
        </Card>
      )}
    </>
  );
};
